import { Box, Combobox, Group, ScrollArea, Text, UnstyledButton, useCombobox } from '@mantine/core';
import { IconCheck, IconChevronDown } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

const AccountDropdown = ({ selectDomain }) => {
  const { user } = useSelector((state) => state.auth);
  const { domain } = useSelector((state) => state.domain);
  const [search, setSearch] = useState('');
  const isSuperAdmin = user.userRoles.some((role) => role.role === 'superAdmin');
  const selectedOptionRef = useRef(null);
  const viewportRef = useRef(null);

  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      combobox.focusTarget();
      setSearch('');
    },
    onDropdownOpen: () => {
      combobox.focusSearchInput();
    },
  });

  // Scroll to selected option when dropdown opens
  useEffect(() => {
    if (combobox.dropdownOpened && selectedOptionRef.current && viewportRef.current) {
      requestAnimationFrame(() => {
        selectedOptionRef.current?.scrollIntoView({ block: 'center' });
      });
    }
  }, [combobox.dropdownOpened]);

  const findDomain = (domainId) => {
    for (const { account } of user.userRoles) {
      if (!account) {
        continue;
      }
      for (const d of account.domains) {
        if (domainId === d.id.toString()) {
          return { ...d, accountId: account.id };
        }
      }
    }
  };

  // Create flat list of all domains with their account info for filtering
  const allDomains = user.userRoles
    .filter((role) => role.account)
    .flatMap((role) =>
      role.account.domains.map((d) => ({
        id: d.id.toString(),
        name: d.name,
        accountName: role.account.name,
        accountId: role.account.id.toString(),
      }))
    );

  // Filter domains based on search (by name, id, or account name)
  const searchTerm = search.toLowerCase().trim();
  const filteredDomains = allDomains.filter(
    (d) =>
      d.name.toLowerCase().includes(searchTerm) ||
      d.id.includes(searchTerm) ||
      d.accountName.toLowerCase().includes(searchTerm) ||
      d.accountId.includes(searchTerm)
  );

  // Group filtered domains by account
  const groupedFiltered = filteredDomains.reduce((acc, d) => {
    if (!acc[d.accountName]) {
      acc[d.accountName] = [];
    }
    acc[d.accountName].push(d);
    return acc;
  }, {});

  // Build options JSX
  const options = Object.entries(groupedFiltered).map(([accountName, domains]) => (
    <Combobox.Group label={accountName} key={accountName}>
      {domains.map((d) => {
        const isSelected = d.id === domain.id.toString();
        return (
          <Combobox.Option
            value={d.id}
            key={d.id}
            ref={isSelected ? selectedOptionRef : undefined}
            style={isSelected ? { backgroundColor: 'var(--mantine-color-primary-light)' } : undefined}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {isSelected && <IconCheck size={14} />}
              {isSuperAdmin ? (
                <Text c="dimmed" size="sm">
                  #{d.id}
                </Text>
              ) : (
                ''
              )}
              {d.name}
            </span>
          </Combobox.Option>
        );
      })}
    </Combobox.Group>
  ));

  const handleOptionSubmit = (value) => {
    const selectedDomain = findDomain(value);

    if (domain.id.toString() === value || !selectedDomain) {
      combobox.closeDropdown();
      return;
    }

    selectDomain(selectedDomain);
    combobox.closeDropdown();
  };

  return (
    <Box data-tour="accountDropdown">
      <Combobox
        store={combobox}
        width={300}
        position="bottom-start"
        withArrow
        withinPortal={false}
        onOptionSubmit={handleOptionSubmit}
      >
        <Combobox.Target withAriaAttributes={false}>
          <UnstyledButton
            onClick={() => combobox.toggleDropdown()}
            py={6}
            px={10}
            className="account-dropdown-button"
          >
            <Group gap={6} wrap="nowrap">
              <Text size="sm" truncate>
                {domain.name}
              </Text>
              <IconChevronDown size={14} color="var(--mantine-color-dimmed)" />
            </Group>
          </UnstyledButton>
        </Combobox.Target>

        <Combobox.Dropdown miw={300}>
          <Combobox.Search
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            placeholder="Search domains..."
          />
          <Combobox.Options>
            <ScrollArea.Autosize type="auto" mah={300} viewportRef={viewportRef}>
              {options.length > 0 ? options : <Combobox.Empty>No domains found</Combobox.Empty>}
            </ScrollArea.Autosize>
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </Box>
  );
};

export default AccountDropdown;
